import React from 'react';
import { Box, CircularProgress, Typography, Button, Alert } from '@mui/material';
import { useAuth } from '../contexts/AuthContext';

const AuthGuard = ({ children }) => {
  const { isAuthenticated, isLoading, authError, authNotice, authDiagnostic, user, login, refreshAuth } = useAuth();

  // Show loading spinner while checking authentication
  if (isLoading) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          gap: 2
        }}
      >
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          Checking authentication...
        </Typography>
      </Box>
    );
  }

  // Show error state if authentication check failed
  if (authError && !isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          gap: 3,
          p: 3
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 600 }}>
          <Typography variant="h6" gutterBottom>
            Authentication Error
          </Typography>
          <Typography variant="body2" color="text.secondary" gutterBottom>
            {authError}
          </Typography>
        </Alert>
        
        <Box sx={{ display: 'flex', gap: 2 }}>
          <Button 
            variant="contained" 
            color="primary" 
            onClick={login}
            size="large"
          >
            Sign In
          </Button>
          <Button 
            variant="outlined" 
            color="secondary" 
            onClick={refreshAuth}
            size="large"
          >
            Retry
          </Button>
        </Box>
      </Box>
    );
  }

  // Show login prompt if not authenticated
  if (!isAuthenticated) {
    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '100vh',
          bgcolor: 'background.default',
          color: 'text.primary',
          gap: 3,
          p: 3
        }}
      >
        <Box sx={{ textAlign: 'center', mb: 2 }}>
          <Typography variant="h3" color="primary" gutterBottom sx={{ fontWeight: 'bold' }}>
            Ever-List
          </Typography>
          <Typography variant="h5" color="text.primary" gutterBottom>
            Inventory Management System
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ maxWidth: 600 }}>
            Please sign in with your Newberry Networks account to access the inventory management system.
          </Typography>
        </Box>

        <Button 
          variant="contained" 
          color="primary" 
          onClick={login}
          size="large"
          sx={{ 
            px: 4, 
            py: 2,
            fontSize: '1.1rem',
            borderRadius: 2
          }}
        >
          {window.location.hostname === 'localhost' ? 'Continue as Local Dev User' : 'Sign In with Microsoft'}
        </Button>

        {authNotice && (
          <Alert severity="warning" sx={{ mt: 2, maxWidth: 700, textAlign: 'left' }}>
            <Typography variant="body2" sx={{ mb: authDiagnostic?.recordedAt ? 1 : 0 }}>
              {authNotice}
            </Typography>
            {authDiagnostic?.recordedAt && (
              <Typography variant="caption" color="text.secondary">
                Diagnostic timestamp: {new Date(authDiagnostic.recordedAt).toLocaleString()}
              </Typography>
            )}
          </Alert>
        )}

        {window.location.hostname === 'localhost' && (
          <Alert severity="info" sx={{ mt: 2, maxWidth: 600 }}>
            <Typography variant="body2">
              <strong>Local Development Mode</strong><br/>
              Click the button above to bypass SSO authentication, or add <code>?bypass_sso=true</code> to the URL.
            </Typography>
          </Alert>
        )}

        <Typography variant="caption" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
          {window.location.hostname === 'localhost' 
            ? 'Local development environment - SSO bypass available'
            : 'Only @newberrynetworks.com accounts are authorized to access this system.'}
        </Typography>
      </Box>
    );
  }

  // Show welcome message for authenticated users (optional)
  console.log('🎉 User authenticated:', user?.userDetails);

  // Render the protected content
  return children;
};

export default AuthGuard;
